import { Constants } from '../constants';
import {
    LoadoutStats,
    KratosTokens,
    ArgonautTokens,
    SimulationInput,
    SimulationConfig,
    PrimordialStats,
    PowerDieColor,
    ArmoryLoadout,
    NamedEntity,
    TriskellionStats,
    LoadoutStatRequirement,
    LoadoutStatsWithRequirements,
    PowerReRollStrategy,
    Argonaut,
    Titan,
    Weapon,
    KratosTable,
    TaggedEntity,
} from '../types';
import { addKratosTokens } from './kratos-tokens';

export function sanitizeInput(input: Partial<SimulationInput>): SimulationInput {
    const sanitizedInput: SimulationInput = {
        primordial: sanitizePrimordialStatsImpl(input.primordial ?? {}, 'Primordial'),
        kratosPool: sanitizeKratosTokensImpl(input.kratosPool ?? {}, 'Kratos Pool'),
        config: sanitizeSimulationConfigImpl(input.config ?? {}, 'Config'),
        loadouts: input.loadouts?.map((l) => sanitizeLoadoutStatsImpl(l, 'Loadout')) ?? [],
    };

    return sanitizedInput;
}

export function sanitizeArmoryLoadout(input: Partial<ArmoryLoadout>): ArmoryLoadout {
    const argonautName = input.argonaut?.name ?? '<unnamed argonaut>';
    input.argonaut ??= { name: argonautName } as Argonaut;
    input.argonaut = sanitizeNamedEntityImpl(input.argonaut, `Argonaut '${argonautName}'`);
    input.argonaut = sanitizeLoadoutStatsImpl(input.argonaut, `Argonaut '${argonautName}' stats`);
    input.argonaut = sanitizeTriskellionStatsImpl(input.argonaut, 0, `Argonaut '${argonautName}' triskellion`);
    input.argonaut = sanitizeArgonautTokensImpl(input.argonaut, `Argonaut '${argonautName}' tokens`);

    const titanName = input.titan?.name ?? '<unnamed titan>';
    input.titan ??= { name: titanName, tags: [], stats: [] } as Titan;
    input.titan = sanitizeNamedEntityImpl(input.titan, `Titan '${titanName}'`);
    input.titan = sanitizeTaggedEntityImpl(input.titan, `Titan '${titanName}'`);
    input.titan.stats = input.titan.stats.map((s) => {
        return sanitizeLoadoutStatsWithRequirementsImpl(s, `Titan '${titanName}' stats`);
    });

    const kratosTableName = input.kratosTable?.name ?? '<unnamed kratos table>';
    input.kratosTable ??= { name: kratosTableName, tags: [], stats: [] } as KratosTable;
    input.kratosTable = sanitizeNamedEntityImpl(input.kratosTable, `Kratos Table '${kratosTableName}'`);
    input.kratosTable = sanitizeTaggedEntityImpl(input.kratosTable, `Kratos Table '${kratosTableName}'`);
    input.kratosTable.stats = input.kratosTable.stats.map((s) => {
        return sanitizeLoadoutStatsWithRequirementsImpl(s, `Kratos Table '${kratosTableName}' stats`);
    });

    const weaponName = input.weapon?.name ?? '<unnamed weapon>';
    input.weapon ??= { name: weaponName, tags: [], stats: [] } as Weapon;
    input.weapon = sanitizeNamedEntityImpl(input.weapon, `Weapon '${weaponName}'`);
    input.weapon = sanitizeTaggedEntityImpl(input.weapon, `Weapon '${weaponName}'`);
    input.weapon.stats = input.weapon.stats.map((s) =>
        sanitizeLoadoutStatsWithRequirementsImpl(s, `Weapon '${weaponName}'`),
    );

    input.gear ??= [];
    input.gear = input.gear.map((gear) => {
        const gearName = gear.name ?? '<unnamed gear>';
        gear = sanitizeNamedEntityImpl(gear, `Gear '${gearName}'`);
        gear = sanitizeTaggedEntityImpl(gear, `Gear '${gearName}'`);
        gear.stats = gear.stats.map((s) => sanitizeLoadoutStatsWithRequirementsImpl(s, `Gear '${gearName}'`));
        return gear;
    });

    input.mnemos ??= [];
    input.mnemos = input.mnemos.map((mnemos) => {
        const mnemosName = mnemos.name ?? '<unnamed mnemos>';
        mnemos = sanitizeNamedEntityImpl(mnemos, `Mnemos '${mnemosName}'`);
        mnemos = sanitizeTaggedEntityImpl(mnemos, `Mnemos '${mnemosName}'`);
        mnemos.stats = mnemos.stats.map((s) => sanitizeLoadoutStatsWithRequirementsImpl(s, `Mnemos '${mnemosName}'`));
        return mnemos;
    });

    return input as ArmoryLoadout;
}

export function sanitizeLoadoutStats(loadoutStats: Partial<LoadoutStats>): LoadoutStats {
    return sanitizeLoadoutStatsImpl(loadoutStats as LoadoutStats, 'Loadout');
}

export function sanitizeLoadoutStatsWithRequirements(
    loadoutStats: Partial<LoadoutStatsWithRequirements>,
): LoadoutStatsWithRequirements {
    return sanitizeLoadoutStatsWithRequirementsImpl(loadoutStats as LoadoutStatsWithRequirements, 'LoadoutStats');
}

/**
 * PRIVATE
 */

function sanitizeNamedEntityImpl<T extends NamedEntity>(input: T, logPrefix: string): T {
    const sanitizedEntity: T = {
        ...input,
        name: input.name?.trim() ?? '',
    };

    validate(sanitizedEntity.name !== '', `${logPrefix}: name cannot be empty or whitespace`);

    return sanitizedEntity;
}

function sanitizeTaggedEntityImpl<T extends TaggedEntity>(input: T, logPrefix: string): T {
    const sanitizedEntity: T = {
        ...input,
        tags: input.tags ?? [],
    };

    sanitizedEntity.tags.forEach((t) => {
        validate(t !== '', `${logPrefix}: tag cannot be empty or whitespace`);
    });

    return sanitizedEntity;
}

function sanitizeArgonautTokensImpl<T extends ArgonautTokens>(input: T, logPrefix: string): T {
    return {
        ...input,
        ambrosia: Math.max(0, input.ambrosia ?? 0),
        despair: Math.max(0, input.despair ?? 0),
        bleed: Math.max(0, input.bleed ?? 0),
        midas: Math.max(0, input.midas ?? 0),
        pain: Math.max(0, input.pain ?? 0),
    };
}

function sanitizeTriskellionStatsImpl<T extends TriskellionStats>(
    input: T,
    defaultValue: number = 0,
    logPrefix: string,
): T {
    const sanitized: T = {
        ...input,
        rage: input.rage ?? defaultValue,
        fate: input.fate ?? defaultValue,
        danger: input.danger ?? defaultValue,
    };

    validate(sanitized.danger >= 0 && sanitized.danger <= 9, `${logPrefix}: danger must be between 0 and 9`);
    validate(sanitized.fate >= 0 && sanitized.fate <= 9, `${logPrefix}: fate must be between 0 and 9`);
    validate(sanitized.rage >= 0 && sanitized.rage <= 9, `${logPrefix}: rage must be between 0 and 9`);

    return sanitized;
}

function sanitizeLoadoutStatsWithRequirementsImpl(
    input: Partial<LoadoutStatsWithRequirements>,
    logPrefix: string,
): LoadoutStatsWithRequirements {
    const sanitized: LoadoutStatsWithRequirements = {
        ...sanitizeLoadoutStatsImpl(input as LoadoutStats, logPrefix),
        requirements: sanitizeLoadoutStatRequirementImpl(input.requirements ?? {}, logPrefix),
    };

    return sanitized;
}

function sanitizeLoadoutStatRequirementImpl(
    input: Partial<LoadoutStatRequirement>,
    logPrefix: string,
): LoadoutStatRequirement {
    const sanitized: LoadoutStatRequirement = {
        minTriskellion: sanitizeTriskellionStatsImpl(input.minTriskellion ?? ({} as TriskellionStats), 0, logPrefix),
        maxTriskellion: sanitizeTriskellionStatsImpl(input.maxTriskellion ?? ({} as TriskellionStats), 9, logPrefix),
    };

    if (input.minArgonautTokens && Object.keys(input.minArgonautTokens).length > 0) {
        sanitized.minArgonautTokens = input.minArgonautTokens;
    }

    return sanitized;
}

function sanitizePrimordialStatsImpl(input: Partial<PrimordialStats>, logPrefix: string): PrimordialStats {
    const sanitized: PrimordialStats = {
        aeonTrespassValue: input.aeonTrespassValue ?? 1,
        aeonTrespassValues: Array.isArray(input.aeonTrespassValues)
            ? input.aeonTrespassValues.map((value) => Math.max(1, value)).sort((a, b) => a - b)
            : undefined,
        toHitValue: input.toHitValue ?? 1,
    };

    validate(sanitized.aeonTrespassValue >= 1, `${logPrefix}: aeonTrespassValue must be greater than or equal to 1`);
    validate(sanitized.toHitValue >= 1, `${logPrefix}: toHitValue must be greater than or equal to 1`);

    return sanitized;
}

function sanitizeSimulationConfigImpl(input: Partial<SimulationConfig>, logPrefix: string): SimulationConfig {
    const sanitized: SimulationConfig = {
        ...Constants.DEFAULT_SIMULATION_CONFIG,
        ...input,
        powerReRollStrategy: Object.values(PowerReRollStrategy).includes(
            input.powerReRollStrategy as PowerReRollStrategy,
        )
            ? (input.powerReRollStrategy as PowerReRollStrategy)
            : Constants.DEFAULT_POWER_REROLL_STRATEGY,
        autoIncrementRage: input.autoIncrementRage ?? false,
    };

    validate(
        sanitized.powerReRollThreshold >= 0,
        `${logPrefix}: powerReRollThreshold must be greater than or equal to 0`,
    );
    validate(sanitized.powerReRollThreshold <= 1, `${logPrefix}: powerReRollThreshold must be less than or equal to 1`);
    validate(
        sanitized.numberOfSimulations >= 1,
        `${logPrefix}: numberOfSimulations must be greater than or equal to 1`,
    );
    validate(sanitized.numberOfSimulations < 10000000, `${logPrefix}: numberOfSimulations must be less than 10000000`);
    validate(
        Object.values(PowerReRollStrategy).includes(sanitized.powerReRollStrategy),
        `${logPrefix}: powerReRollStrategy must be a supported strategy`,
    );

    return sanitized;
}

function sanitizeLoadoutStatsImpl<T extends LoadoutStats>(input: T, logPrefix: string): T {
    const sanitized: T = {
        ...input,
        attackDice: input.attackDice ?? 0,
        attackReRolls: input.attackReRolls ?? 0,
        autoHits: input.autoHits ?? 0,
        autoTokens: sanitizeKratosTokensImpl(input.autoTokens ?? {}, logPrefix),
        maxHits: input.maxHits ?? 0,
        name: input.name ?? 'Stat Name',
        overridePowerDice: input.overridePowerDice ?? false,
        perHitPowerDice: sanitizePowerDieColorsImpl(input.perHitPowerDice ?? []),
        perHitPowerDiceOverride: (input.perHitPowerDiceOverride ?? Array(input.maxHits ?? 1).fill([])).map(
            sanitizePowerDieColorsImpl,
        ),
        powerBonus: input.powerBonus ?? 0,
        powerDice: sanitizePowerDieColorsImpl(input.powerDice ?? []),
        powerReRolls: input.powerReRolls ?? 0,
        precisionBonus: input.precisionBonus ?? 0,
    };

    validate(
        sanitized.maxHits <= sanitized.attackDice,
        `${logPrefix}: maxHits (${sanitized.maxHits}) must be less than or equal to attackDice (${sanitized.attackDice})`,
    );

    if (sanitized.perHitPowerDiceOverride.length > 0) {
        validate(
            sanitized.perHitPowerDiceOverride.length >= sanitized.maxHits,
            `${logPrefix}: perHitPowerDiceOverride must have at least as many dice sets (${sanitized.perHitPowerDiceOverride.length}) as the number of maxHits (${sanitized.maxHits})`,
        );
    }

    return sanitized;
}

function sanitizePowerDieColorsImpl(input: PowerDieColor[]): PowerDieColor[] {
    return input.filter((die) => Object.values(PowerDieColor).includes(die));
}

function sanitizeKratosTokensImpl(kratosPool: Partial<KratosTokens>, logPrefix: string): KratosTokens {
    const sanitized = addKratosTokens(Constants.EMPTY_KRATOS_POOL, kratosPool);

    for (const [key, value] of Object.entries(sanitized)) {
        validate(value >= 0, `${logPrefix}: ${key} tokens must be greater than or equal to zero`);
    }

    return sanitized;
}

function validate(condition: boolean, message?: string): void {
    if (!condition) {
        console.error(message);
    }
}
